import { useState } from "react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/contexts/AuthContext";
import { canWrite } from "@/lib/rbac";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  ChevronLeft,
  ChevronRight,
  PhoneIncoming,
  CheckCircle2,
  Clock,
  MessageSquare,
  BarChart2,
  UserCheck,
} from "lucide-react";
import { toast } from "sonner";

type PeriodFilter = "7d" | "30d" | "90d" | "all";
type StatusFilter = "all" | "converted" | "pending" | "abandoned";
type QualificationFilter = "all" | "PENDING" | "QUALIFIED" | "NOT_QUALIFIED" | "DUPLICATE";

const QUALIFICATION_LABELS: Record<string, string> = {
  PENDING: "En attente",
  QUALIFIED: "Qualifié",
  NOT_QUALIFIED: "Non qualifié",
  DUPLICATE: "Doublon",
};

const QUALIFICATION_VARIANTS: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
  PENDING: "outline",
  QUALIFIED: "default",
  NOT_QUALIFIED: "destructive",
  DUPLICATE: "secondary",
};

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function LpLeads() {
  const { user } = useAuth();
  const canAct = canWrite(user?.staffRole);
  const [page, setPage] = useState(1);
  const [periodFilter, setPeriodFilter] = useState<PeriodFilter>("30d");
  const [utmSourceFilter, setUtmSourceFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [qualificationFilter, setQualificationFilter] = useState<QualificationFilter>("all");

  const LIMIT = 20;
  const utils = trpc.useUtils();

  const now = new Date();
  function dateFrom(): string | undefined {
    if (periodFilter === "all") return undefined;
    const days = periodFilter === "7d" ? 7 : periodFilter === "30d" ? 30 : 90;
    const d = new Date(now.getTime() - days * 86400 * 1000);
    return d.toISOString();
  }

  const { data, isLoading } = trpc.admin.travauxbtp.listLpLeads.useQuery({
    page,
    limit: LIMIT,
    completedOnly: statusFilter === "converted" ? true : undefined,
    abandonedOnly: statusFilter === "abandoned" ? true : undefined,
    utmSource: utmSourceFilter !== "all" ? utmSourceFilter : undefined,
    qualificationStatus: qualificationFilter !== "all" ? qualificationFilter : undefined,
    dateFrom: dateFrom(),
  });

  const { data: stats } = trpc.admin.travauxbtp.getLpLeadStats.useQuery();

  const { data: staffList } = trpc.admin.staff.list.useQuery(undefined, {
    enabled: user?.staffRole === "SUPER_ADMIN",
  });

  const qualifyMutation = trpc.admin.travauxbtp.qualifyLpLead.useMutation({
    onSuccess: () => {
      utils.admin.travauxbtp.listLpLeads.invalidate();
      toast.success("Qualification mise à jour");
    },
  });

  const assignMutation = trpc.admin.travauxbtp.assignLpLead.useMutation({
    onSuccess: () => {
      utils.admin.travauxbtp.listLpLeads.invalidate();
      toast.success("Assignation mise à jour");
    },
  });

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = data?.totalPages ?? 1;

  // Build UTM source breakdown from items (client-side)
  const utmSourceCounts = items.reduce<Record<string, number>>((acc, lead: any) => {
    const src = lead.utmSource ?? "Direct";
    acc[src] = (acc[src] ?? 0) + 1;
    return acc;
  }, {});
  const maxCount = Math.max(...Object.values(utmSourceCounts), 1);
  const utmSources = Object.entries(utmSourceCounts).sort((a, b) => b[1] - a[1]);

  // UTM sources for filter dropdown (from current items)
  const uniqueUtmSources = [...new Set(items.map((l: any) => l.utmSource).filter(Boolean))];

  function formatDate(d: string | Date | null | undefined) {
    if (!d) return "—";
    return new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <PhoneIncoming className="h-6 w-6 text-primary" />
            LP Leads
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Leads issus des landing pages TravauxBTP — relance SMS automatique
          </p>
        </div>

        {/* KPI cards */}
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          <Card>
            <CardContent className="pt-5 pb-4">
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-xs text-muted-foreground mb-1">Total leads</div>
                  <div className="text-2xl font-bold">{stats?.total ?? "—"}</div>
                </div>
                <div className="p-2 rounded-lg bg-primary/10 text-primary">
                  <PhoneIncoming className="h-5 w-5" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-5 pb-4">
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-xs text-muted-foreground mb-1">Taux conversion</div>
                  <div className="text-2xl font-bold text-success">{stats?.convRate ?? "—"}%</div>
                </div>
                <div className="p-2 rounded-lg bg-success/10 text-success">
                  <CheckCircle2 className="h-5 w-5" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-5 pb-4">
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-xs text-muted-foreground mb-1">SMS J0 en attente</div>
                  <div className={`text-2xl font-bold ${(stats?.smsJ0Pending ?? 0) > 0 ? "text-warning" : ""}`}>
                    {stats?.smsJ0Pending ?? "—"}
                  </div>
                </div>
                <div className="p-2 rounded-lg bg-warning/10 text-warning">
                  <MessageSquare className="h-5 w-5" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-5 pb-4">
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-xs text-muted-foreground mb-1">SMS J1 en attente</div>
                  <div className={`text-2xl font-bold ${(stats?.smsJ1Pending ?? 0) > 0 ? "text-warning" : ""}`}>
                    {stats?.smsJ1Pending ?? "—"}
                  </div>
                </div>
                <div className="p-2 rounded-lg bg-muted text-muted-foreground">
                  <Clock className="h-5 w-5" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* UTM Source bar chart */}
        {utmSources.length > 0 && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <BarChart2 className="h-4 w-4 text-primary" />
                Volume par source UTM (vue courante)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {utmSources.map(([src, count]) => (
                  <div key={src} className="flex items-center gap-3">
                    <span className="text-xs text-muted-foreground w-32 truncate" title={src}>
                      {src}
                    </span>
                    <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full bg-primary"
                        style={{ width: `${Math.round((count / maxCount) * 100)}%` }}
                      />
                    </div>
                    <span className="text-xs font-medium w-8 text-right">{count}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Filters */}
        <div className="flex items-center gap-3 flex-wrap">
          <Select value={periodFilter} onValueChange={(v) => { setPeriodFilter(v as PeriodFilter); setPage(1); }}>
            <SelectTrigger className="w-[130px]" aria-label="Période">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7d">7 derniers jours</SelectItem>
              <SelectItem value="30d">30 derniers jours</SelectItem>
              <SelectItem value="90d">90 derniers jours</SelectItem>
              <SelectItem value="all">Tout</SelectItem>
            </SelectContent>
          </Select>

          <Select value={utmSourceFilter} onValueChange={(v) => { setUtmSourceFilter(v); setPage(1); }}>
            <SelectTrigger className="w-[160px]" aria-label="Source UTM">
              <SelectValue placeholder="Source UTM" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes sources</SelectItem>
              {uniqueUtmSources.map((src) => (
                <SelectItem key={src} value={src}>{src}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v as StatusFilter); setPage(1); }}>
            <SelectTrigger className="w-[150px]" aria-label="Statut">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous statuts</SelectItem>
              <SelectItem value="converted">Convertis</SelectItem>
              <SelectItem value="pending">En attente</SelectItem>
              <SelectItem value="abandoned">Abandonnés</SelectItem>
            </SelectContent>
          </Select>

          <Select value={qualificationFilter} onValueChange={(v) => { setQualificationFilter(v as QualificationFilter); setPage(1); }}>
            <SelectTrigger className="w-[160px]" aria-label="Qualification">
              <SelectValue placeholder="Qualification" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes qualif.</SelectItem>
              <SelectItem value="PENDING">En attente</SelectItem>
              <SelectItem value="QUALIFIED">Qualifié</SelectItem>
              <SelectItem value="NOT_QUALIFIED">Non qualifié</SelectItem>
              <SelectItem value="DUPLICATE">Doublon</SelectItem>
            </SelectContent>
          </Select>

          <span className="text-sm text-muted-foreground ml-auto">{total} lead{total !== 1 ? "s" : ""}</span>
        </div>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="py-10 text-center text-muted-foreground text-sm">Chargement…</div>
            ) : items.length === 0 ? (
              <div className="py-10 text-center text-muted-foreground text-sm">Aucun lead</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Code postal</TableHead>
                    <TableHead>UTM Source</TableHead>
                    <TableHead>Qualification</TableHead>
                    <TableHead>Assigné à</TableHead>
                    <TableHead>SMS J0</TableHead>
                    <TableHead>SMS J1</TableHead>
                    <TableHead>Statut</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((lead: any) => {
                    const isConverted = !!lead.completedAt;
                    const qualStatus = lead.qualificationStatus ?? "PENDING";
                    const assignedStaff = lead.AssignedToStaff;
                    return (
                      <TableRow key={lead.id}>
                        <TableCell className="text-sm text-muted-foreground">
                          {formatDate(lead.createdAt)}
                        </TableCell>
                        <TableCell className="text-sm font-medium">{lead.codePostal}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {lead.utmSource ?? "—"}
                        </TableCell>
                        <TableCell>
                          <Select
                            value={qualStatus}
                            disabled={!canAct}
                            onValueChange={(v) =>
                              qualifyMutation.mutate({
                                lpLeadId: lead.id,
                                qualificationStatus: v as "PENDING" | "QUALIFIED" | "NOT_QUALIFIED" | "DUPLICATE",
                              })
                            }
                          >
                            <SelectTrigger className="h-7 w-[130px] text-xs">
                              <Badge variant={QUALIFICATION_VARIANTS[qualStatus] ?? "outline"} className="text-xs">
                                {QUALIFICATION_LABELS[qualStatus] ?? qualStatus}
                              </Badge>
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="PENDING">En attente</SelectItem>
                              <SelectItem value="QUALIFIED">Qualifié</SelectItem>
                              <SelectItem value="NOT_QUALIFIED">Non qualifié</SelectItem>
                              <SelectItem value="DUPLICATE">Doublon</SelectItem>
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell>
                          {staffList ? (
                            <Select
                              value={lead.assignedToStaffId ?? "unassigned"}
                              disabled={!canAct}
                              onValueChange={(v) =>
                                assignMutation.mutate({
                                  lpLeadId: lead.id,
                                  assignedToStaffId: v === "unassigned" ? null : v,
                                })
                              }
                            >
                              <SelectTrigger className="h-7 w-[140px] text-xs">
                                <SelectValue>
                                  {assignedStaff
                                    ? `${assignedStaff.firstName ?? ""} ${assignedStaff.lastName ?? ""}`.trim()
                                    : "Non assigné"}
                                </SelectValue>
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="unassigned">Non assigné</SelectItem>
                                {staffList.map((s: any) => (
                                  <SelectItem key={s.id} value={s.id}>
                                    {s.firstName ?? ""} {s.lastName ?? s.email}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : assignedStaff ? (
                            <div className="flex items-center gap-1 text-xs">
                              <UserCheck className="h-3 w-3 text-primary" />
                              {`${assignedStaff.firstName ?? ""} ${assignedStaff.lastName ?? ""}`.trim()}
                            </div>
                          ) : (
                            <span className="text-xs text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {lead.smsJ0SentAt ? (
                            <Badge variant="outline" className="text-xs bg-success/10 text-success border-success/20">
                              Envoyé
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-xs text-muted-foreground">
                              En attente
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {lead.smsJ1SentAt ? (
                            <Badge variant="outline" className="text-xs bg-success/10 text-success border-success/20">
                              Envoyé
                            </Badge>
                          ) : lead.smsJ0SentAt ? (
                            <Badge variant="outline" className="text-xs text-warning border-warning/20 bg-warning/10">
                              En attente
                            </Badge>
                          ) : (
                            <span className="text-xs text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {isConverted ? (
                            <Badge variant="outline" className="text-xs bg-success/10 text-success border-success/20">
                              Converti
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-xs">
                              En attente
                            </Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Précédent
            </Button>
            <span className="text-sm text-muted-foreground">Page {page} / {totalPages}</span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
            >
              Suivant
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
